import * as React from 'react';
import {
	Grid,
	makeStyles,
	Theme,
	createStyles,
	TextField,
	Paper,
	Accordion as ExpansionPanel,
	AccordionSummary as ExpansionPanelSummary,
	AccordionDetails as ExpansionPanelDetails,
	Button,
	Typography,
	Menu,
	MenuItem,
} from '@material-ui/core';
import {
	ExpandMore,
	Add,
	Receipt,
	PictureAsPdf,
	Mail,
	FileCopy,
} from '@material-ui/icons';
import { useApi } from '../../@context/Api';
import { throttle } from 'lodash';
import toPrettyCurrency from '../../@utils/toPrettyCurrency';
import { API_URL } from '../../App';
import { useState, FC, useEffect, useContext } from 'react';
import { EntryContext } from '@context/Entry';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		listItem: {
			paddingLeft: 0,
			flexWrap: 'wrap',
		},
		utilitiesTitleContainer: {
			// display: 'flex'
		},
		utilityItitle: {
			display: 'flex',
			paddingRight: theme.spacing(2),
		},
		legend: {
			// color: '#858585',
			paddingRight: theme.spacing(1),
		},
		value: {
			fontWeight: 600,
		},
		field: {
			marginBottom: theme.spacing(1),
			'&:last-child': {
				marginBottom: 0,
			},
		},
		iframeContainer: {
			width: '100%',
			height: '60vh',
		},
		iframe: {
			width: '100%',
			height: '100%',
			border: 0,
		},
		btnAddContainer: {
			width: '100%',
			padding: theme.spacing(0, 1, 0, 1),
			display: 'flex',
		},
		btnAdd: {
			marginRight: 'auto',
			boxShadow: 'none',
		},
		btnLegend: {
			marginLeft: 10,
		},
		'@media screen and (max-width: 1430px)': {
			btnLegend: {
				marginLeft: 0,
				display: 'none',
			},
		},
		invoiceButton: {
			marginLeft: 10,
			boxShadow: 'none',
			'&:hover': {
				color: 'white',
			},
		},
		templateRemoveItemBtn: {
			marginRight: 10,
		},
		templateSaveItemBtn: {
			backgroundColor: 'green',
		},
	})
);
type Props = {
	entry: Offer;
	changes: Partial<Offer>;
	refresh: () => void;
	path: string;
};

const BillingItemEditor: FC<{
	item: BillingItem;
	refresh: () => Promise<void>;
}> = ({ item, refresh }) => {
	const classes = useStyles();
	const [changes, setChanges] = useState<Partial<BillingItem>>({});
	const preview = { ...item, ...changes };
	const { save, deleteEntry } = useApi();

	const onInputChange = (prop: keyof BillingItem) => (
		e: React.ChangeEvent<HTMLInputElement>
	) => setChanges({ ...changes, [prop]: e.target.value });

	const onNumberInputChange = (prop: keyof BillingItem) => (
		e: React.ChangeEvent<HTMLInputElement>
	) => setChanges({ ...changes, [prop]: e.target.valueAsNumber });

	const _save = async () => {
		await save('BillingItem', item.Id, changes);
		await refresh();
		setChanges({});
	};

	const _delete = async () => {
		await deleteEntry(item.Type, item.Id);
		await refresh();
	};

	return (
		<Grid container={true} spacing={3}>
			<Grid item={true} xs={6}>
				<TextField
					classes={{ root: classes.field }}
					label='Title'
					fullWidth={true}
					value={preview.Title}
					onChange={onInputChange('Title')}
				/>
			</Grid>
			<Grid item={true} xs={6}>
				<TextField
					classes={{ root: classes.field }}
					label='Amount'
					type='number'
					value={preview.Amount}
					fullWidth={true}
					onChange={onNumberInputChange('Amount')}
				/>
			</Grid>
			<Grid item={true} xs={12}>
				<TextField
					classes={{ root: classes.field }}
					label='Description'
					value={preview.Description}
					onChange={onInputChange('Description')}
					multiline={true}
					fullWidth={true}
				/>
			</Grid>

			<Grid item={true} xs={12}>
				<Button
					size='small'
					color='secondary'
					variant='contained'
					onClick={_delete}
					classes={{ root: classes.templateRemoveItemBtn }}
				>
					remove
				</Button>
				{Object.keys(changes).length > 0 ? (
					<Button
						size='small'
						color='secondary'
						variant='contained'
						onClick={_save}
						classes={{ root: classes.templateSaveItemBtn }}
					>
						save
					</Button>
				) : null}
			</Grid>
		</Grid>
	);
};

const OfferUtilities: FC<Props> = ({
	entry,
	changes,
	refresh,
	children,
	path,
}) => {
	const classes = useStyles();
	const { Type, Id, Currency } = entry;
	const { fetch, create, getAll, createShipment } = useApi();
	const { open } = useContext(EntryContext);
	const [BillingItems, setBillingItems] = useState<BillingItem[]>([]);
	const [itemTemplates, setItemTemplates] = useState<BillingItemTemplate[]>([]);
	const [expandedIndex, setExpandedIndex] = useState<number>();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const previewWindow = React.useRef<HTMLIFrameElement>(null);

	const refreshItems = async () => {
		const options: RequestInit = {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
		};

		const items = await fetch<BillingItem[]>(
			`billing/${Type}/${Id}/items`,
			options
		);

		setBillingItems(items);
	};

	const refreshItemTemplates = async () => {
		getAll('BillingItemTemplate').then((items) =>
			setItemTemplates(items as BillingItemTemplate[])
		);
	};

	const updatePreview = (chgs: Partial<Offer>, billingItems: BillingItem[]) => {
		if (previewWindow && previewWindow.current) {
			const patchDocument = Object.keys(chgs).map((k) => ({
				op: 'replace',
				path: `/${k}`,
				value: chgs[k],
			}));

			const options: RequestInit = {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ changes: patchDocument, billingItems }),
			};
			fetch<string>(`billing/${Type}/${Id}/preview`, options).then(
				(preview) =>
					(previewWindow!.current!.contentDocument!.body.innerHTML = preview)
			);
		}
	};

	const throttled = React.useRef(throttle(updatePreview, 1000));

	useEffect(() => {
		if (previewWindow) {
			throttled.current(changes, BillingItems);
		}
	}, [BillingItems, previewWindow, changes]);

	useEffect(() => {
		refreshItems();
		refreshItemTemplates();
	}, []);

	const createOfferEmail = async () => {
		const result = await createShipment('EmailShipment', entry);
		await open(`${path}/${result.Id}`, result.Id, result.Type);
		refresh();
	};

	const makeInvoice = async () => {
		const result = await create('Invoice', entry, undefined, {});
		await open(`${path}/${result.Id}`, result.Id, result.Type);
	};

	const duplicate = async () => {
		const result = await create(Type, entry, undefined, {});
		await open(`${path}/${result.Id}`, result.Id, result.Type);
	};

	const openAdd = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const closeAdd = () => {
		setAnchorEl(null);
	};

	const add = async (chg?: Partial<BillingItem>) => {
		closeAdd();
		await create('BillingItem', entry, undefined, chg);
		await refreshItems();
		setExpandedIndex(BillingItems.length);
	};

	const AddTemplate = (template: BillingItemTemplate) => (
		event: React.MouseEvent<HTMLLIElement>
	) => {
		add({
			Title: template.Title,
			Description: template.Description,
			Amount: template.Amount,
		});
	};
	const itemsTotal = BillingItems.reduce((sum, i) => sum + (Number(i.Amount) || 0), 0);
	const effectiveTotal =
		Number(changes.Amount !== undefined ? changes.Amount : entry.Amount) || 0;
	const totalMismatch =
		BillingItems.length > 0 &&
		Math.abs(effectiveTotal - itemsTotal) >= 0.005;

	return (
		<Grid item={true} xs={12}>
			<Grid container={true} spacing={3}>
				<Grid item={true} xs={12}>
					<Grid container={true} spacing={2}>
						<Grid item={true} xs={12}>
							<Paper classes={{ root: classes.iframeContainer }} elevation={2}>
								<iframe ref={previewWindow} className={classes.iframe} />
							</Paper>
						</Grid>
					</Grid>
				</Grid>
				<Grid item={true} xs={12}>
					{totalMismatch && (
						<Paper
							elevation={0}
							style={{
								background: '#fff3cd',
								color: '#856404',
								border: '1px solid #ffeeba',
								borderRadius: 4,
								padding: '8px 12px',
								marginBottom: 12,
							}}
						>
							<Typography variant='body2'>
								⚠ Total ({toPrettyCurrency(effectiveTotal || 0, Currency)}) does not
								match the sum of the items ({toPrettyCurrency(itemsTotal, Currency)}).
							</Typography>
						</Paper>
					)}
					{BillingItems.map((item, i) => {
						const expanded = expandedIndex === i;
						const { Title, Amount } = item;
						return (
							<ExpansionPanel
								key={item.Id}
								expanded={expanded}
								onChange={() => setExpandedIndex(expanded ? undefined : i)}
							>
								<ExpansionPanelSummary expandIcon={<ExpandMore />}>
									<div className={classes.utilitiesTitleContainer}>
										<Typography className={classes.legend}>{Title}</Typography>
										{Amount !== 0 && (
											<Typography className={classes.value}>
												{toPrettyCurrency(Amount, Currency)}
											</Typography>
										)}
										{Amount === 0 && (
											<Typography className={classes.value}>
												{toPrettyCurrency(Amount, Currency)}
											</Typography>
										)}
									</div>
								</ExpansionPanelSummary>
								<ExpansionPanelDetails>
									<BillingItemEditor refresh={refreshItems} item={item} />
								</ExpansionPanelDetails>
							</ExpansionPanel>
						);
					})}
				</Grid>
				<Grid item={true} xs={12}>
					<Grid container={true} spacing={3}>
						<div className={classes.btnAddContainer}>
							<Button
								className={classes.btnAdd}
								aria-label='Remove'
								onClick={openAdd}
								color='primary'
								variant='contained'
								size='small'
							>
								<Add />
								<span className={classes.btnLegend}>Add</span>
							</Button>
							<Menu
								id='simple-menu'
								anchorEl={anchorEl}
								keepMounted={true}
								open={Boolean(anchorEl)}
								onClose={closeAdd}
							>
								<MenuItem onClick={() => add()}>Empty</MenuItem>
								{itemTemplates.map((i) => (
									<MenuItem key={i.Id} onClick={AddTemplate(i)}>
										{i.TemplateTitle}
									</MenuItem>
								))}
							</Menu>

							<Button
								className={classes!.invoiceButton}
								variant='contained'
								size='small'
								href={`${API_URL}/billing/${Type}/${Id}/pdf`}
								target='_blank'
							>
								<PictureAsPdf />
								<span className={classes.btnLegend}>PDF</span>
							</Button>
							<Button
								className={classes!.invoiceButton}
								variant='contained'
								size='small'
								onClick={createOfferEmail}
							>
								<Mail />
								<span className={classes.btnLegend}>Email</span>
							</Button>
							<Button
								className={classes!.invoiceButton}
								variant='contained'
								size='small'
								color='primary'
								onClick={makeInvoice}
							>
								<Receipt />
								<span className={classes.btnLegend}>make invoice</span>
							</Button>
							<Button
								className={classes!.invoiceButton}
								variant='contained'
								size='small'
								color='primary'
								onClick={duplicate}
							>
								<FileCopy />
								<span className={classes.btnLegend}>duplicate</span>
							</Button>
						</div>
					</Grid>
				</Grid>
				{children}
			</Grid>
		</Grid>
	);
};

export default OfferUtilities;
